import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/* Ebb cart: AJAX add-to-cart from the Ebb buy box (opens the drawer) + one-click pack upgrade inside the cart.
   2026-10-27: add first then remove on upgrade, double-submit guard, inline error, drawer readiness wait,
   empty-state class kept in sync, singular/plural cart label. */

const routes = () =>
  (typeof Theme !== 'undefined' && Theme.routes) || {
    cart_add_url: '/cart/add.js',
    cart_change_url: '/cart/change.js',
    cart_update_url: '/cart/update.js',
  };

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

const json = (res) => res.json();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchCart() {
  try {
    return await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(json);
  } catch (e) {
    return null;
  }
}

/* Keep the drawer's server-rendered empty/filled state in sync (the section morph only touches the inner content). */
function syncDrawerState(itemCount) {
  if (typeof itemCount !== 'number') return;
  document.querySelectorAll('cart-drawer-component dialog').forEach((dlg) => {
    dlg.classList.toggle('cart-drawer--empty', itemCount === 0);
    dlg.setAttribute('aria-labelledby', itemCount === 0 ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  });
}

/* Wait (max 2 s) for the drawer module, which is loaded with a low priority. */
async function drawerReady() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return null;
  await Promise.race([customElements.whenDefined('cart-drawer-component'), sleep(2000)]);
  return drawer;
}

async function openDrawer(drawer) {
  if (!drawer) return;
  const dlg = drawer.querySelector('dialog');
  if (!dlg) return;
  /* Horizon opens the drawer itself on cart:update when auto-open is set; only step in if it did not happen. */
  await sleep(400);
  if (!dlg.open && typeof drawer.showDialog === 'function') drawer.showDialog();
}

/* ---- pack upgrade (drawer + cart page): add the new pack first, then remove the old line ---- */
async function upgrade(btn) {
  const key = btn.dataset.key;
  const to = btn.dataset.to;
  if (!key || !to) return;
  const comp = btn.closest('cart-items-component');
  const sid = comp?.dataset.sectionId || sectionIds()[0];
  const ids = sectionIds();
  const card = btn.closest('[data-ebb-upsell]');
  card?.querySelectorAll('button').forEach((b) => (b.disabled = true));
  comp?.classList.add('cart-items-disabled');
  try {
    const r1 = await fetch(routes().cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(to), quantity: 1 }] }),
    }).then(json);
    if (r1.status) throw new Error(r1.description || r1.message);
    const r2 = await fetch(routes().cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0, sections: ids.join(','), sections_url: location.pathname }),
    }).then(json);
    if (r2.status) throw new Error(r2.description || r2.message);
    const cart = r2.item_count !== undefined ? r2 : await fetchCart();
    const count = cart?.item_count ?? 0;
    if (comp) {
      comp.dispatchEvent(new CartUpdateEvent(cart ?? {}, sid, { source: 'ebb-cart', itemCount: count, sections: r2.sections }));
    }
    for (const id of ids) {
      const html = r2.sections?.[id];
      if (html) await morphSection(id, html, { mode: id === sid && comp?.hasAttribute('data-drawer') ? 'hydration' : 'full' });
    }
    syncDrawerState(count);
  } catch (e) {
    console.error('[ebb-cart] upgrade failed', e);
    card?.querySelectorAll('button').forEach((b) => (b.disabled = false));
    location.reload();
  } finally {
    comp?.classList.remove('cart-items-disabled');
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-ebb-upgrade]') : null;
  if (btn) {
    e.preventDefault();
    upgrade(btn);
  }
});

/* ---- Ebb buy box: AJAX add + open drawer ---- */
function showError(form, message) {
  let el = form.querySelector('[data-ebb-error]');
  if (!el) {
    el = document.createElement('p');
    el.setAttribute('data-ebb-error', '');
    el.setAttribute('role', 'alert');
    el.style.cssText = 'margin:10px 0 0;font-size:14px;line-height:1.4;color:#9b2c2c;text-align:center';
    const btn = form.querySelector('button[type="submit"]');
    (btn ? btn.parentNode : form).insertBefore(el, btn ? btn.nextSibling : null);
  }
  el.textContent = message;
}

function wireForm(form) {
  if (form.dataset.ebbWired) return;
  form.dataset.ebbWired = '1';
  const formId = form.getAttribute('id') || '';
  form.addEventListener('submit', async (e) => {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return; // no drawer on this page: classic submit
    e.preventDefault();
    if (form.dataset.busy) return; // double submit guard
    const fd = new FormData(form);
    fd.delete('_mock_plan');
    if (!fd.get('id')) return HTMLFormElement.prototype.submit.call(form);
    form.dataset.busy = '1';
    const ids = sectionIds();
    fd.append('sections', ids.join(','));
    fd.append('sections_url', location.pathname);
    const btns = [...form.querySelectorAll('button[type="submit"]'), ...(formId ? document.querySelectorAll(`button[form="${formId}"]`) : [])];
    btns.forEach((b) => { b.disabled = true; b.classList.add('is-busy'); b.setAttribute('aria-busy', 'true'); });
    const err = form.querySelector('[data-ebb-error]');
    if (err) err.textContent = '';
    let res;
    try {
      res = await fetch(routes().cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: fd,
      }).then(json);
    } catch (networkError) {
      console.error('[ebb-cart] ajax add failed, falling back', networkError);
      delete form.dataset.busy;
      return HTMLFormElement.prototype.submit.call(form);
    }
    try {
      if (res.status) {
        showError(form, res.description || res.message || 'Something went wrong. Please try again.');
        return;
      }
      const cart = await fetchCart();
      const count = cart?.item_count ?? 1;
      form.dispatchEvent(
        new CartAddEvent(cart ?? {}, formId, {
          source: 'ebb-pdp',
          itemCount: count,
          productId: form.dataset.productId,
          sections: res.sections,
        })
      );
      syncDrawerState(count);
      await openDrawer(await drawerReady());
    } finally {
      delete form.dataset.busy;
      btns.forEach((b) => { b.disabled = false; b.classList.remove('is-busy'); b.removeAttribute('aria-busy'); });
    }
  });
}
document.querySelectorAll('[data-pdp-form]').forEach(wireForm);

/* keep the Ebb header count and the drawer state in sync when the header section is not re-rendered */
document.addEventListener('cart:update', (e) => {
  const n = e.detail?.resource?.item_count ?? e.detail?.data?.itemCount;
  if (typeof n !== 'number') return;
  document.querySelectorAll('.ebb-hdr__cart').forEach((el) => {
    el.setAttribute('aria-label', `Cart, ${n} ${n === 1 ? 'item' : 'items'}`);
    let c = el.querySelector('.ebb-hdr__count');
    if (n > 0) {
      if (!c) { c = document.createElement('span'); c.className = 'ebb-hdr__count'; el.appendChild(c); }
      c.textContent = String(n);
    } else if (c) c.remove();
  });
  syncDrawerState(n);
});
